import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Input } from "antd";
import { GoogleMap, Libraries, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { AimOutlined, CloseOutlined, CompressOutlined, EnvironmentOutlined, ExpandOutlined } from "@ant-design/icons";
import useScreenSize from "../../hooks/useScreenSize";

type Location = {
    lat: number;
    lng: number;
};

interface LocationPickerProps {
    value?: string | null;
    onChange?: (location: string | null) => void;
}
const containerStyle = {
    width: "100%",
    height: "calc(100% - 60px)",
};

const dhakaLocation = { lat: 23.8103, lng: 90.4125 };
const googleMapLibraries: Libraries = ["places"]; // Required for Autocomplete

const LocationPicker: React.FC<LocationPickerProps> = ({
    value = null,
    onChange,
}) => {
    const [lat, lng] = value ? value.split(",").map(Number) : [null, null];
    const defaultLocation: Location | null = lat && lng ? { lat, lng } : null;
    const { isMobile } = useScreenSize();
    const [maximized, setMaximized] = useState(false);
    const [position, setPosition] = useState<Location | null>(defaultLocation);
    const [selectedPlace, setSelectedPlace] = useState<{ label: any; value: any } | null>({
        label: "Dhaka Bangladesh",
        value: defaultLocation || dhakaLocation
    });
    const [open, setOpen] = useState(false);
    // Store map instance
    const [map, setMap] = useState<any>(null);
    const onLoad = useCallback((mapInstance: any) => setMap(mapInstance), []);
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
        libraries: googleMapLibraries, 
    });

    const onGooglePlaceChange = (val: any) => {
        if (!window.google) return;

        const placeId = val.value.place_id;
        const service = new window.google.maps.places.PlacesService(
            document.createElement("div")
        );

        service.getDetails({ placeId }, (place: any, status: any) => {
            if (
                status === window.google.maps.places.PlacesServiceStatus.OK &&
                place.geometry
            ) {
                const newPos = {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                };
                setPosition(newPos);
                map?.panTo(newPos); // recenter map
                setSelectedPlace({
                    label: val.label,
                    value: { place_id: placeId, lat: newPos.lat, lng: newPos.lng },
                });
            }
        });
    };

    // When user clicks/drags marker on map
    const onGoogleMapClick = (lat: number, lng: number) => {
        const newPos = { lat, lng };
        setPosition(newPos);

        if (!window.google || !window.google.maps.Geocoder) return;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: newPos }, (results, status) => {
            if (status === "OK" && results && results[0]) {
                setSelectedPlace({
                    label: results[0].formatted_address,
                    value: { place_id: results[0].place_id, lat, lng },
                });
            }
        });
    };

    const onLocationSelect = () => {
        if (position) {
            if (onChange) onChange(`${position.lat?.toFixed(6)},${position.lng?.toFixed(6)}`);
        }
        else {
            setPosition(dhakaLocation);
            if (onChange) onChange(`${dhakaLocation.lat?.toFixed(6)},${dhakaLocation.lng?.toFixed(6)}`);
        }
        setOpen(false);
    };

    const onClearLocation = () => {
        setPosition(null);
        if (onChange) onChange(null);
    };

    const setCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const lat = pos.coords.latitude;
                    const lng = pos.coords.longitude;
                    setPosition({ lat, lng });
                    onChange?.(`${lat},${lng}`);
                },
                () => alert("Unable to get current location")
            );
        } else {
            alert("Geolocation not supported");
        }
    };

    useEffect(() => {
        if (defaultLocation)
            onGoogleMapClick(defaultLocation.lat, defaultLocation.lng);
    }, [defaultLocation]);

    return (
        <>
            <Input
                value={position ? `${position?.lat?.toFixed(6)}, ${position?.lng?.toFixed(6)}` : ""}
                readOnly
                addonAfter={
                    <Button type="link" onClick={() => setOpen(true)} className="pl-2 pr-2 pt-0 pb-0">
                        <EnvironmentOutlined />
                    </Button>
                }
                suffix={position ? <CloseOutlined size={4} onClick={onClearLocation} /> : null}
            />
            <Modal
                className="location-picker"
                open={open}
                onCancel={() => setOpen(false)}
                title={"Choose Location"}
                footer={[
                    <Button key="cancel" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>,
                    <Button key="ok" type="primary" onClick={onLocationSelect}>
                        Select
                    </Button>,
                ]}
                closeIcon={
                    <div style={{ display: "flex", gap: 8, alignItems: "center", top: 0 }}>
                        {/* Maximize / Minimize button */}
                        {maximized ? (
                            <CompressOutlined
                                style={{ fontSize: 18 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMaximized(false);
                                }}
                            />
                        ) : (
                            <ExpandOutlined
                                style={{ fontSize: 18 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMaximized(true);
                                }}
                            />
                        )}
                        {/* Default close button */}
                        <CloseOutlined
                            style={{ fontSize: 18 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpen(false);
                            }}
                        />
                    </div>
                }
                width={maximized ? "100%" : 800}
                style={{ top: maximized ? 0 : 100, padding: 0 }}
                styles={{ body: { height: maximized ? "calc(100vh - 120px)" : "40vh" } }}
            >
                <div style={{ height: maximized ? "calc(100vh - 120px)" : "40vh", overflow: "auto" }}>
                    {isLoaded && <>
                        <div className={`float-left ${isMobile ? "w-full" : "mb-[10px] w-[70%]"}`} >
                            <GooglePlacesAutocomplete
                                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                                selectProps={{
                                    value: selectedPlace,
                                    onChange: onGooglePlaceChange,
                                    placeholder: "Search location...",
                                }}

                            />
                        </div>
                        <div className={`float-right items-center ${isMobile ? "w-full mb-[10px]" : "w-[30%]"}`}>
                            <span className="float-left p-[5px]">{position?.lat && position?.lng ? `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}` : ""}</span>
                            <Button onClick={() => setCurrentLocation()} className="float-right" type="link" title="Set current location"><AimOutlined style={{ fontSize: 20 }} /></Button>
                        </div>
                        <GoogleMap
                            mapContainerStyle={containerStyle}
                            center={position || dhakaLocation as any}
                            zoom={12}
                            onLoad={onLoad}
                            onClick={(e) => onGoogleMapClick(e.latLng!.lat(), e.latLng!.lng())}
                        >
                            <MarkerF
                                position={position || dhakaLocation as any}
                                draggable
                                onDragEnd={(e) =>
                                    onGoogleMapClick(e.latLng!.lat(), e.latLng!.lng())
                                }
                            />
                        </GoogleMap>
                    </>}
                    {!isLoaded && "Loading..."}
                </div>
            </Modal>

        </>
    );
};

export default LocationPicker;
